import {
  ArrowRight,
  Circle,
  Diamond,
  Eraser,
  Feather,
  Hand,
  Database,
  Hexagon,
  Highlighter,
  Italic,
  Lasso,
  Minus,
  MousePointer2,
  PaintBucket,
  Minimize2,
  MoreHorizontal,
  PenTool,
  Plus,
  Redo2,
  Shapes,
  Sparkles,
  Spline,
  Pill,
  Square,
  Table,
  Type,
  Undo2,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";

import { DraggablePanel } from "@/components/reader/DraggablePanel";
import { Popover, PopoverContent, PopoverTrigger, Tip } from "@/components/ui/overlays";
import { Separator } from "@/components/ui/primitives";
import { cn } from "@/lib/utils";
import {
  ADAPTIVE_INK,
  ERASER_RADIUS_POINTS,
  MAX_COLOR_SLOTS,
  FONT_SIZE_POINTS,
  HIGHLIGHTER_WIDTH_POINTS,
  PEN_WIDTH_POINTS,
  fontPtToRel,
  isShapeTool,
  relToFontPt,
  relToStrokePt,
  strokePtToRel,
  resolveInk,
  slotToolFor,
  SHAPE_DIGITS,
  SHAPE_TOOLS,
  useAnnotationTool,
  type AnnotationTool,
  type EraseMode,
  type ShapeTool,
} from "@/stores/annotation-tool";
import { ModeSwitch } from "@/components/reader/ModeSwitch";
import { useShell } from "@/stores/shell";

import { fontLabel, fontStack } from "./fonts";
import type { SmoothingPreset } from "./ink";
import type { InkModel } from "./ink-model";

/** Subscribe to a model's undo/redo availability so the toolbar buttons enable
 * and disable as the stack grows/shrinks (the model emits `undoState` on every
 * transaction, undo, and redo). */
function useInkHistory(model: InkModel | null | undefined): { canUndo: boolean; canRedo: boolean } {
  const [state, setState] = useState(() => ({
    canUndo: model?.canUndo ?? false,
    canRedo: model?.canRedo ?? false,
  }));
  useEffect(() => {
    if (!model) {
      setState({ canUndo: false, canRedo: false });
      return;
    }
    const sync = () => setState({ canUndo: model.canUndo, canRedo: model.canRedo });
    sync();
    return model.on("undoState", sync);
  }, [model]);
  return state;
}

// Tools grouped as they read on the island (Excalidraw-style): pointer · marks ·
// text. Separators divide the groups; the order is grouping-only (e2e targets by
// aria-label, never by position).
const TOOL_GROUPS: { id: AnnotationTool; label: string; Icon: typeof PenTool; key?: string }[][] = [
  [{ id: "select", label: "Select / move", Icon: MousePointer2, key: "1" }],
  [
    { id: "pen", label: "Pen", Icon: PenTool, key: "2" },
    { id: "highlighter", label: "Highlighter", Icon: Highlighter, key: "3" },
    { id: "eraser", label: "Eraser", Icon: Eraser, key: "4" },
    { id: "lasso", label: "Circle & ask AI", Icon: Lasso, key: "5" },
    // The bucket sits with the marks — it lays colour down, and it is the
    // companion to the shapes it usually fills. It carries NO digit: 1-7 are
    // taken and 8/9/0 belong to the three shapes, so a badge here would have to
    // steal one. It is one tap from the row and from the shape palette.
    { id: "fill", label: "Fill", Icon: PaintBucket },
  ],
  [{ id: "text", label: "Text", Icon: Type, key: "6" }],
];

/** digit -> tool, derived from the groups so the badge and the shortcut can
 * never disagree. The shape digit is not here — it resolves to whichever shape
 * is currently chosen, and repeating it cycles (see SHAPE_KEY). */
const TOOL_BY_KEY: Record<string, AnnotationTool> = Object.fromEntries(
  TOOL_GROUPS.flat()
    .filter((t) => !!t.key)
    .map((t) => [t.key!, t.id]),
);

// All ten drawn shapes share ONE button. Adding an icon per shape to the row
// would widen the island by several hundred px — it is already ~755px and scrolls on a
// tablet in portrait — and the island's width is a hard layout invariant (a
// centred row means any width change slides every icon). So the button shows
// the shape you last drew, one tap selects it, a second tap opens the picker,
// and the digit cycles. Same "tap again to change" gesture as the colour slots.
const SHAPE_KEY = "7";

const SHAPE_META: Record<ShapeTool, { label: string; Icon: typeof PenTool }> = {
  rect: { label: "Rectangle", Icon: Square },
  ellipse: { label: "Ellipse", Icon: Circle },
  diamond: { label: "Diamond", Icon: Diamond },
  line: { label: "Line", Icon: Minus },
  arrow: { label: "Arrow", Icon: ArrowRight },
  // Flowchart parts. Lucide has no parallelogram, so the italic slab reads as
  // the lean; the rest map onto real glyphs.
  parallelogram: { label: "Parallelogram", Icon: Italic },
  terminator: { label: "Terminator", Icon: Pill },
  hexagon: { label: "Hexagon", Icon: Hexagon },
  cylinder: { label: "Cylinder", Icon: Database },
  table: { label: "Table", Icon: Table },
};

/** What the floating palette holds: every shape and line, plus the bucket that
 * fills them — a shape-heavy workflow reaches for all of these together, and the
 * bucket has no digit of its own, so this is where it is always one tap away. */
const PALETTE_TOOLS: AnnotationTool[] = [...SHAPE_TOOLS, "fill"];
const PALETTE_META: Record<string, { label: string; Icon: typeof PenTool }> = {
  ...SHAPE_META,
  fill: { label: "Fill", Icon: PaintBucket },
};

/** digit -> shape, for the three shapes that own a key of their own (8/9/0 —
 * the digits the core tools left free). Derived from the store's table so the
 * badge and the shortcut can never disagree, exactly like TOOL_BY_KEY. */
const SHAPE_BY_KEY: Record<string, ShapeTool> = Object.fromEntries(
  Object.entries(SHAPE_DIGITS).map(([shape, key]) => [key, shape as ShapeTool]),
);

/** Is the user typing into something? Number keys must never steal a keystroke
 * from a text annotation, a size field, or the rename box. */
function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}

// One square, optically-centered island button (the user's complaint was
// off-centre glyphs); active = quiet violet tint, no heavy border.
// 32px, not 36. The row has to hold Read/Write and Exit in fullscreen and
// still fit a phone, and trimming 4px from each of ~16 controls buys that
// width back without going under the 32px a thumb can reliably hit.
const ISLAND_BTN = "grid h-8 w-8 shrink-0 place-items-center rounded-md transition-colors";
const ICON = "h-[18px] w-[18px]";
const INACTIVE = "text-muted-foreground hover:bg-accent hover:text-foreground";
const toolCls = (active: boolean) => cn(ISLAND_BTN, active ? "bg-primary/15 text-primary" : INACTIVE);

const SELECT_CLS = "rounded border border-border bg-input px-1 py-0.5 text-[10px] text-muted-foreground";

/** A thin vertical divider between logical groups on the island. */
function Sep() {
  return <Separator orientation="vertical" className="mx-0.5 h-6 self-center" />;
}

// A representative on-screen page width (px) used to preview a stroke's REAL
// on-page thickness in the size wedge — so the triangle's width is the width the
// stylus actually lays down, proportional to the page (not an arbitrary scale).
const PREVIEW_PAGE_PX = 480;
// The thinnest bar that still reads as a line, and the thickest that still reads
// as a stroke rather than an oval (the pill is only 26px long).
const WEDGE_MIN_PX = 1.5;
const WEDGE_MAX_PX = 20;
// Where the curve saturates, in TRUE preview px. Set by the largest preset on
// ANY scale — the highlighter's 48pt, at 48/612*480 ≈ 37.6px — not the pen's
// 36pt: saturating at the pen's max flattened the highlighter's top two presets
// against each other, which is the same defect one scale over.
const WEDGE_REF_PX = 38;
/**
 * A preset's bar height. MONOTONIC across the whole range, which a true-to-page
 * scale is not at these sizes: `pt/612*480` makes 0.5pt just 0.39px, so a plain
 * `max(3, …)` floor rendered 0.5 / 1 / 1.5 / 2 / 3 pt at *identically* 3px and
 * 4pt at 3.14px — six presets no one could tell apart — while at the other end
 * a 20px cap flattened 24pt and 36pt together.
 *
 * The bar's job is relative weight at a glance, so it is a perceptual scale
 * (gamma < 1 spreads the fine end where the differences matter and compresses
 * the heavy end where they don't), not a ruler. Exactness is still available and
 * unambiguous: every preset carries a `${pt} pt` tooltip and the number field
 * beside the wedge shows and sets the real value.
 */
export const wedgeThickness = (rel: number) => {
  const truePx = Math.max(0, rel * PREVIEW_PAGE_PX);
  const t = Math.min(1, truePx / WEDGE_REF_PX);
  return WEDGE_MIN_PX + (WEDGE_MAX_PX - WEDGE_MIN_PX) * Math.pow(t, 0.55);
};

/** A short horizontal line at a stroke's real on-page thickness — what the pen
 * actually lays down, read at a glance. (A dot was honest about diameter but
 * hard to judge at the fine end; a line reads as a stroke.) */
function StrokeBar({ thickness, color }: { thickness: number; color: string }) {
  return (
    <span
      className="block w-[26px] shrink-0 rounded-full"
      style={{ height: thickness, background: color }}
      aria-hidden
    />
  );
}

/** A numeric size field (points) with free entry + a datalist of common values —
 * the Word / Google Docs pattern. Inline in the island (the ink e2e fills it
 * directly); since the island OVERLAYS the surface, it never shifts the canvas. */
function NumberField({
  value,
  onCommit,
  label,
  list,
  min,
  max,
  step,
}: {
  value: number;
  onCommit: (n: number) => void;
  label: string;
  list: string;
  min: number;
  max: number;
  step: number;
}) {
  const [text, setText] = useState(String(value));
  useEffect(() => setText(String(value)), [value]);
  return (
    <Tip label={`${label} (points)`}>
      <input
        type="number"
        inputMode="decimal"
        min={min}
        max={max}
        step={step}
        value={text}
        list={list}
        aria-label={label}
        onChange={(e) => {
          setText(e.target.value);
          const n = Number(e.target.value);
          if (e.target.value !== "" && Number.isFinite(n)) onCommit(n);
        }}
        onBlur={() => setText(String(value))}
        className={cn("h-8 w-14 shrink-0 tabular-nums", SELECT_CLS)}
      />
    </Tip>
  );
}

/** The size dropdown: a row of DOTS at the stroke's ACTUAL on-page diameter.
 * A round nib's width IS a circle, so the affordance is the thing itself rather
 * than an abstract wedge you have to decode (this replaced a rotated triangle,
 * which said nothing about width at all). The trigger is itself a dot at the
 * current width, so the toolbar always shows what you are about to lay down.
 * Anchored BELOW the island as a popover — it never widens the row. */
function SizeWedge({
  valueRel,
  onPickRel,
  presetsPt,
  ptToRel,
  color,
  kind,
}: {
  valueRel: number;
  onPickRel: (rel: number) => void;
  presetsPt: number[];
  ptToRel: (pt: number) => number;
  color: string;
  kind: string;
}) {
  return (
    <Popover>
      <Tip label={`${kind} size`}>
        <PopoverTrigger asChild>
          <button
            aria-label={`${kind} size scale`}
            className={cn(ISLAND_BTN, INACTIVE, "ring-1 ring-border")}
          >
            <StrokeBar thickness={wedgeThickness(valueRel)} color={color} />
          </button>
        </PopoverTrigger>
      </Tip>
      <PopoverContent align="center" className="w-auto">
        <div className="mb-2 text-[10px] uppercase tracking-wide text-muted-foreground">
          {kind} size
        </div>
        <div className="grid w-[252px] grid-cols-6 gap-1">
          {presetsPt.map((pt) => {
            const rel = ptToRel(pt);
            const active = Math.abs(rel - valueRel) < 1e-6;
            return (
              <Tip key={pt} label={`${pt} pt`}>
                <button
                  onClick={() => onPickRel(rel)}
                  aria-label={`${kind} ${pt} point`}
                  className={cn(
                    "grid h-9 w-10 place-items-center rounded-md transition-colors",
                    active ? "bg-primary/15 ring-1 ring-primary" : "hover:bg-accent",
                  )}
                >
                  <StrokeBar thickness={wedgeThickness(rel)} color={color} />
                </button>
              </Tip>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** The curated colour palette + a free colour picker. Tapping a swatch selects
 * it; the active swatch shows a × to unpin it; "+" pins the current colour.
 * Lives inside the colour popover (its trigger, the current-colour swatch, stays
 * on the island). */
function ColorPalette({ onPick }: { onPick?: (color: string) => void }) {
  const color = useAnnotationTool((s) => s.color);
  const palette = useAnnotationTool((s) => s.palette);
  const setColor = useAnnotationTool((s) => s.setColor);
  const addPaletteColor = useAnnotationTool((s) => s.addPaletteColor);
  const removePaletteColor = useAnnotationTool((s) => s.removePaletteColor);
  const inPalette = palette.some((p) => p.toLowerCase() === color.toLowerCase());
  const pick = onPick ?? setColor;

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1.5">
      {palette.map((c) => {
        const active = c.toLowerCase() === color.toLowerCase();
        return (
          <span key={c} className="relative shrink-0">
            <Tip label={c}>
              <button
                onClick={() => pick(c)}
                aria-label={`Use colour ${c}`}
                className={cn(
                  "h-6 w-6 rounded-full border transition-transform",
                  active ? "border-primary ring-1 ring-primary" : "border-border hover:scale-110",
                )}
                style={{ background: c }}
              />
            </Tip>
            {active && palette.length > 1 && (
              <button
                onClick={() => removePaletteColor(c)}
                aria-label={`Unpin colour ${c}`}
                className="absolute -right-1 -top-1 grid h-3 w-3 place-items-center rounded-full bg-background text-muted-foreground hover:text-foreground"
              >
                <X className="h-2 w-2" />
              </button>
            )}
          </span>
        );
      })}
      <Tip label="Pick a colour">
        <input
          type="color"
          value={color}
          onChange={(e) => pick(e.target.value)}
          aria-label="Annotation color"
          className="h-6 w-6 shrink-0 cursor-pointer rounded border border-border bg-transparent p-0"
        />
      </Tip>
      <Tip label="Keep this colour on the toolbar">
        <button
          onClick={() => addPaletteColor(color)}
          disabled={inPalette}
          aria-label="Keep colour"
          className="grid h-6 w-6 shrink-0 place-items-center rounded text-muted-foreground hover:text-foreground disabled:opacity-30"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
      </Tip>
    </div>
  );
}

/** The three quick-colour slots. One tap selects; a tap on the ALREADY-selected
 * slot opens its editor, so customizing costs no extra chrome on the row.
 *
 * A slot may hold {@link ADAPTIVE_INK}, which renders near-black on light paper
 * and near-white on dark — so "just write" stays legible when a note's paper
 * flips, instead of vanishing into it. The swatch always shows the RESOLVED
 * colour, i.e. what the pen will actually lay down on this page. */
function ColorSlots({ paper }: { paper: "light" | "dark" }) {
  const tool = useAnnotationTool((s) => s.tool);
  const slotTool = slotToolFor(tool);
  const slots = useAnnotationTool((s) => s.colorSlots)[slotTool];
  const color = useAnnotationTool((s) => s.color);
  const setColor = useAnnotationTool((s) => s.setColor);
  const setColorSlot = useAnnotationTool((s) => s.setColorSlot);
  // Which slot's editor is open. CONTROLLED rather than letting the popover
  // toggle itself: the trigger has two jobs (select, or edit when already
  // selected) and suppressing the library's own toggle with preventDefault is
  // not something to rely on — a tap would both select AND open.
  const [editing, setEditing] = useState<number | null>(null);

  return (
    <>
      {slots.map((stored, i) => {
        const shown = resolveInk(stored, paper);
        const selected = shown.toLowerCase() === color.toLowerCase();
        const label =
          stored === ADAPTIVE_INK
            ? "Adaptive ink — black on light paper, white on dark"
            : stored;
        const setSlot = (c: string) => {
          setColorSlot(slotTool, i, c);
          setColor(resolveInk(c, paper));
        };
        return (
          <Popover
            key={i}
            open={editing === i}
            onOpenChange={(open) => {
              // A tap on an UNSELECTED slot only picks its colour — refuse the
              // library's own open here rather than trying to suppress the
              // click, so the two intents can't race.
              if (open && !selected) return;
              setEditing(open ? i : null);
            }}
          >
            <Tip label={selected ? `${label} · tap again to change` : label}>
              <PopoverTrigger asChild onClick={() => !selected && setColor(shown)}>
                <button
                  aria-label={`Colour ${i + 1}`}
                  aria-pressed={selected}
                  className={cn(ISLAND_BTN, selected ? "bg-primary/15" : INACTIVE)}
                >
                  <span
                    className={cn(
                      "h-[18px] w-[18px] rounded-full border shadow-inner",
                      selected ? "border-primary ring-1 ring-primary" : "border-border",
                    )}
                    style={{ background: shown }}
                  />
                </button>
              </PopoverTrigger>
            </Tip>
            <PopoverContent align="center" className="w-auto max-w-[240px] space-y-2">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Colour {i + 1}
              </div>
              <button
                onClick={() => setSlot(ADAPTIVE_INK)}
                aria-label="Adaptive ink"
                aria-pressed={stored === ADAPTIVE_INK}
                className={cn(
                  "flex w-full items-center gap-2 rounded px-1.5 py-1 text-left text-[11px] transition-colors",
                  stored === ADAPTIVE_INK
                    ? "bg-primary/15 text-primary"
                    : "text-muted-foreground hover:bg-accent",
                )}
              >
                <span
                  className="h-4 w-4 shrink-0 rounded-full border border-border"
                  style={{ background: "linear-gradient(135deg, #111111 50%, #e7e5da 50%)" }}
                />
                Adaptive — follows the paper
              </button>
              <ColorPalette onPick={setSlot} />
            </PopoverContent>
          </Popover>
        );
      })}
    </>
  );
}

/** The free colour picker, inline on the row. The three slots are for the
 * colours you reach for constantly; this is for the one you need right now,
 * without opening anything. It lives in the space the pen and highlighter leave
 * in the reserved options slot, so it costs no width. */
function AnyColorPicker() {
  const color = useAnnotationTool((s) => s.color);
  const setColor = useAnnotationTool((s) => s.setColor);
  return (
    <Tip label="Any colour">
      <input
        type="color"
        value={/^#[0-9a-f]{6}$/i.test(color) ? color : "#111111"}
        onChange={(e) => setColor(e.target.value)}
        aria-label="Any colour"
        className="h-7 w-7 shrink-0 cursor-pointer rounded border border-border bg-transparent p-0"
      />
    </Tip>
  );
}

/** Whole-stroke vs. area erasing — a small inline segmented control (the ink
 * e2e clicks "Erase Area" directly, so it stays on the island). */
function EraseModeToggle({ mode, onChange }: { mode: EraseMode; onChange: (m: EraseMode) => void }) {
  const opt = (m: EraseMode, label: string) => (
    <button
      key={m}
      onClick={() => onChange(m)}
      aria-label={`Erase ${label}`}
      aria-pressed={mode === m}
      className={cn(
        "rounded px-1.5 py-1 text-[10px] transition-colors",
        mode === m ? "bg-primary/20 text-primary" : "text-muted-foreground hover:text-foreground",
      )}
    >
      {label}
    </button>
  );
  return (
    <div className="flex h-8 shrink-0 items-center rounded border border-border bg-background p-0.5">
      {opt("stroke", "Stroke")}
      {opt("area", "Area")}
    </div>
  );
}

/** Per-stroke smoothing preset (plan §3.7) — a small three-way segmented control
 * in the pen options popover, styled like the eraser-mode toggle. Maps to
 * perfect-freehand params in ink.ts; a session preference, never persisted onto
 * an annotation. */
function SmoothingControl({
  value,
  onChange,
}: {
  value: SmoothingPreset;
  onChange: (p: SmoothingPreset) => void;
}) {
  const opt = (p: SmoothingPreset, label: string) => (
    <button
      key={p}
      onClick={() => onChange(p)}
      aria-label={`Smoothing ${label}`}
      aria-pressed={value === p}
      className={cn(
        "flex-1 rounded px-1.5 py-0.5 text-[10px] transition-colors",
        value === p ? "bg-primary/20 text-primary" : "text-muted-foreground hover:text-foreground",
      )}
    >
      {label}
    </button>
  );
  return (
    <div className="space-y-1">
      <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <Spline className="h-3.5 w-3.5" /> Smoothing
      </span>
      <div className="flex items-center rounded border border-border bg-background p-0.5">
        {opt("precise", "Precise")}
        {opt("balanced", "Balanced")}
        {opt("smooth", "Smooth")}
      </div>
    </div>
  );
}

/** A tool button on the island. */
function ToolButton({
  on,
  onClick,
  label,
  Icon,
  keyHint,
}: {
  on: boolean;
  onClick: () => void;
  label: string;
  Icon: typeof PenTool;
  /** The digit that selects this tool, shown as a subscript badge the way
   * Excalidraw labels its toolbar — the shortcut teaches itself. */
  keyHint?: string;
}) {
  return (
    <Tip label={keyHint ? `${label} — ${keyHint}` : label}>
      <button onClick={onClick} aria-label={label} aria-pressed={on} className={cn(toolCls(on), "relative")}>
        <Icon className={ICON} />
        {keyHint && (
          <span
            className="pointer-events-none absolute bottom-0.5 right-1 font-mono text-[9px] leading-none text-muted-foreground"
            aria-hidden
          >
            {keyHint}
          </span>
        )}
      </button>
    </Tip>
  );
}

/** The shapes button: one slot on the row holding whichever shape you last drew.
 *
 * A tap on an UNSELECTED button just selects that shape; a tap when it is
 * already active opens the picker. That is the colour slots' gesture, and for
 * the same reason — the popover is CONTROLLED and refuses to open unless the
 * button is already selected, because suppressing Base UI's own toggle with
 * preventDefault does not work (a tap both selected AND opened). */
function ShapePicker() {
  const tool = useAnnotationTool((s) => s.tool);
  const lastShape = useAnnotationTool((s) => s.lastShape);
  const setTool = useAnnotationTool((s) => s.setTool);
  const [open, setOpen] = useState(false);
  const active = isShapeTool(tool);
  const shown = active ? tool : lastShape;
  const { label, Icon } = SHAPE_META[shown];

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next && !active) return;
        setOpen(next);
      }}
    >
      <Tip label={active ? `${label} — tap again to change shape` : `${label} — ${SHAPE_KEY}`}>
        <PopoverTrigger asChild onClick={() => !active && setTool(shown)}>
          <button
            aria-label="Shapes"
            aria-pressed={active}
            className={cn(toolCls(active), "relative")}
          >
            <Icon className={ICON} />
            <span
              className="pointer-events-none absolute bottom-0.5 right-1 font-mono text-[9px] leading-none text-muted-foreground"
              aria-hidden
            >
              {SHAPE_KEY}
            </span>
          </button>
        </PopoverTrigger>
      </Tip>
      <PopoverContent align="center" className="w-auto">
        <div className="mb-2 text-[10px] uppercase tracking-wide text-muted-foreground">Shape</div>
        <div className="flex items-center gap-1">
          {SHAPE_TOOLS.map((s) => {
            const meta = SHAPE_META[s];
            return (
              <Tip key={s} label={meta.label}>
                <button
                  onClick={() => {
                    setTool(s);
                    setOpen(false);
                  }}
                  aria-label={meta.label}
                  aria-pressed={tool === s}
                  className={cn(
                    "grid h-9 w-9 place-items-center rounded-md transition-colors",
                    tool === s ? "bg-primary/15 text-primary ring-1 ring-primary" : "hover:bg-accent",
                  )}
                >
                  <meta.Icon className={ICON} />
                </button>
              </Tip>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Fill, dashes and rounded corners for shapes. Applies to the NEXT shape you
 * draw and to a SELECTED one — restyling what you already drew is the same
 * gesture as setting it beforehand. Outline-only, solid, square is the default,
 * so every one of these is an addition rather than a surprise. */
function ShapeStyleControls() {
  const tool = useAnnotationTool((s) => s.tool);
  const fill = useAnnotationTool((s) => s.shapeFill);
  const dash = useAnnotationTool((s) => s.shapeDash);
  const corner = useAnnotationTool((s) => s.shapeCorner);
  const setFill = useAnnotationTool((s) => s.setShapeFill);
  const setDash = useAnnotationTool((s) => s.setShapeDash);
  const setCorner = useAnnotationTool((s) => s.setShapeCorner);
  return (
    <div className="space-y-2 border-b border-border pb-2">
      <div className="flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <PaintBucket className="h-3.5 w-3.5" /> Fill
        </span>
        <span className="flex items-center gap-1">
          <input
            type="color"
            value={fill && /^#[0-9a-f]{6}$/i.test(fill) ? fill : "#ffe9a8"}
            onChange={(e) => setFill(e.target.value)}
            aria-label="Shape fill colour"
            className="h-5 w-5 cursor-pointer rounded border border-border bg-transparent p-0"
          />
          <button
            onClick={() => setFill(null)}
            disabled={!fill}
            aria-label="No shape fill"
            aria-pressed={!fill}
            className={cn(
              "rounded px-1.5 py-0.5 text-[10px] transition-colors disabled:opacity-100",
              fill ? "text-muted-foreground hover:text-foreground" : "bg-primary/20 text-primary",
            )}
          >
            None
          </button>
        </span>
      </div>
      <ToggleRow on={dash} onClick={() => setDash(!dash)} label="Dashed outline" Icon={Spline} />
      {/* Rounding is a rectangle's own property; the other figures have no
          corners to round, so the control says so rather than doing nothing. */}
      <label
        className={cn(
          "flex items-center justify-between gap-2 text-[11px] text-muted-foreground",
          tool !== "rect" && "opacity-40",
        )}
      >
        Rounded
        <input
          type="range"
          min={0}
          max={0.5}
          step={0.05}
          value={corner}
          disabled={tool !== "rect"}
          aria-label="Rectangle corner rounding"
          onChange={(e) => setCorner(Number(e.target.value))}
          className="w-24 accent-[hsl(var(--primary))]"
        />
      </label>
    </div>
  );
}

/** The floating shape palette — every shape and line on one draggable card.
 *
 * The toolbar's single shape button is right for occasional use (it costs one
 * slot and keeps the island's width fixed), but it is two taps deep for someone
 * whose work IS shapes. This is the standing surface for them: opened from the
 * ⋯ menu, dragged wherever it suits the page, and left there — it persists, so
 * a refresh doesn't put it away. Every shape is one tap; the first three also
 * carry their own digit. */
function ShapePalette() {
  const tool = useAnnotationTool((s) => s.tool);
  const setTool = useAnnotationTool((s) => s.setTool);
  const close = useAnnotationTool((s) => s.setShapePaletteOpen);
  return (
    <DraggablePanel
      title={
        <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest">
          <Shapes className="h-3 w-3" /> Shapes
        </span>
      }
      label="Shapes"
      onClose={() => close(false)}
      initial={{ x: 16, y: 72 }}
      width={196}
    >
      <div className="grid grid-cols-3 gap-1 p-2">
        {PALETTE_TOOLS.map((s) => {
          const { label, Icon } = PALETTE_META[s]!;
          const digit = SHAPE_DIGITS[s as ShapeTool];
          return (
            <Tip key={s} label={digit ? `${label} — ${digit}` : label}>
              <button
                onClick={() => setTool(s)}
                aria-label={label}
                aria-pressed={tool === s}
                className={cn(
                  "relative grid h-11 w-full place-items-center rounded-md transition-colors",
                  tool === s ? "bg-primary/15 text-primary ring-1 ring-primary" : INACTIVE,
                )}
              >
                <Icon className={ICON} />
                {digit && (
                  <span
                    className="pointer-events-none absolute bottom-0.5 right-1 font-mono text-[9px] leading-none text-muted-foreground"
                    aria-hidden
                  >
                    {digit}
                  </span>
                )}
              </button>
            </Tip>
          );
        })}
      </div>
    </DraggablePanel>
  );
}

/** "Add colour" — promotes the CURRENT colour into a new slot on the row, so a
 * colour found with the free picker can be kept without displacing one of the
 * three. Lives under the ⋯ menu because it is a one-off act of curation, not
 * something you reach for while writing. Adding one DOES widen the row (the
 * slots are the only part of the toolbar that grows), which is why it is a
 * deliberate, capped action rather than automatic. */
function AddColorSlot() {
  const tool = useAnnotationTool((s) => s.tool);
  const slotTool = slotToolFor(tool);
  const slots = useAnnotationTool((s) => s.colorSlots)[slotTool];
  const color = useAnnotationTool((s) => s.color);
  const addColorSlot = useAnnotationTool((s) => s.addColorSlot);
  const full = slots.length >= MAX_COLOR_SLOTS;
  const already = slots.some((c) => c.toLowerCase() === color.toLowerCase());
  return (
    <button
      onClick={() => addColorSlot(slotTool, color)}
      disabled={full || already}
      aria-label="Add colour slot"
      className="flex w-full items-center justify-between gap-2 text-[11px] text-muted-foreground disabled:opacity-40"
    >
      <span className="flex items-center gap-1.5">
        <Plus className="h-3.5 w-3.5" /> Add colour
      </span>
      <span
        className="h-4 w-4 shrink-0 rounded-full border border-border"
        style={{ background: color }}
      />
    </button>
  );
}

/** A labelled on/off row inside the "More" dropdown. */
function ToggleRow({
  on,
  onClick,
  label,
  Icon,
}: {
  on: boolean;
  onClick: () => void;
  label: string;
  Icon: typeof PenTool;
}) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      aria-pressed={on}
      className="flex w-full items-center justify-between gap-2 text-[11px] text-muted-foreground"
    >
      <span className="flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5" /> {label}
      </span>
      <span
        className={cn(
          "rounded px-1.5 py-0.5 text-[10px]",
          on ? "bg-primary/20 text-primary" : "bg-background text-muted-foreground",
        )}
      >
        {on ? "On" : "Off"}
      </span>
    </button>
  );
}

/**
 * Markup toolbar — an Excalidraw-style floating ISLAND at the top of the drawing
 * surface (Wave 3 Part E + Part A). It OVERLAYS the canvas (absolute, centred),
 * so entering annotate mode, switching tools, or opening a picker never changes
 * the drawing surface's box — the viewport-stability invariant is structural, not
 * reactive. Collapsed it is a single "Annotate" pill; editing, it expands in
 * place into one row of square tool buttons + colour/size controls, with the
 * secondary toggles and every picker anchored BELOW the island as popovers.
 */
export function AnnotationToolbar({
  fonts,
  model,
  paper = "light",
}: {
  fonts: string[];
  model?: InkModel | null;
  /** The page's lightness, so adaptive ink resolves to what will actually be
   * legible on it. Defaults to light (PDFs, and every paper style but a dark
   * canvas background). */
  paper?: "light" | "dark";
}) {
  const { canUndo, canRedo } = useInkHistory(model);
  const fullscreen = useShell((s) => s.fullscreen);
  const tool = useAnnotationTool((s) => s.tool);
  const color = useAnnotationTool((s) => s.color);
  const fontFamily = useAnnotationTool((s) => s.fontFamily);
  const fontSizeRel = useAnnotationTool((s) => s.fontSizeRel);
  const strokeRel = useAnnotationTool((s) => s.strokeRel);
  const highlighterRel = useAnnotationTool((s) => s.highlighterRel);
  const eraserRel = useAnnotationTool((s) => s.eraserRel);
  const eraseMode = useAnnotationTool((s) => s.eraseMode);
  const pressure = useAnnotationTool((s) => s.pressure);
  const smoothing = useAnnotationTool((s) => s.smoothing);
  const palmReject = useAnnotationTool((s) => s.palmReject);
  const shapePaletteOpen = useAnnotationTool((s) => s.shapePaletteOpen);
  const setTool = useAnnotationTool((s) => s.setTool);
  const setFont = useAnnotationTool((s) => s.setFont);
  const setFontSize = useAnnotationTool((s) => s.setFontSize);
  const setStrokeWidth = useAnnotationTool((s) => s.setStrokeWidth);
  const setHighlighterWidth = useAnnotationTool((s) => s.setHighlighterWidth);
  const setEraserRadius = useAnnotationTool((s) => s.setEraserRadius);
  const setEraseMode = useAnnotationTool((s) => s.setEraseMode);
  const setPressure = useAnnotationTool((s) => s.setPressure);
  const setSmoothing = useAnnotationTool((s) => s.setSmoothing);
  const setPalmReject = useAnnotationTool((s) => s.setPalmReject);
  const setShapePaletteOpen = useAnnotationTool((s) => s.setShapePaletteOpen);

  // The overlay anchor: centred at the top of the reader region, above the
  // drawing surface. pointer-events pass through the empty margins to the canvas;
  // only the island itself is interactive.
  // Number-key tool selection (Excalidraw's habit, ported): 1 select, 2 pen,
  // 3 highlighter, 4 eraser, 5 lasso, 6 text. Only while ALREADY annotating —
  // pressing a digit whilst reading a PDF should not drop you into a pen — and
  // never when a keystroke belongs to a text field. Bare digits only, so
  // browser and app shortcuts on the modifiers stay untouched.
  useEffect(() => {
    if (tool === "none") return;
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || isTypingTarget(e.target)) return;
      if (e.key === SHAPE_KEY) {
        // First press picks the remembered shape; pressing it again while a
        // shape is live cycles to the next one (Excalidraw's habit), which is
        // how all ten shapes stay reachable from one key.
        e.preventDefault();
        const s = useAnnotationTool.getState();
        const i = SHAPE_TOOLS.indexOf(s.tool as ShapeTool);
        setTool(i === -1 ? s.lastShape : SHAPE_TOOLS[(i + 1) % SHAPE_TOOLS.length]!);
        return;
      }
      // 8 / 9 / 0 go straight to a shape — no cycling, no flyout. These are the
      // digits the core tools left free, and they exist for a shape-heavy
      // workflow that reaches for the same three constantly.
      const shape = SHAPE_BY_KEY[e.key];
      if (shape) {
        e.preventDefault();
        setTool(shape);
        return;
      }
      const next = TOOL_BY_KEY[e.key];
      if (!next) return;
      e.preventDefault();
      setTool(next);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [tool, setTool]);

  // The island is centred in the surface MINUS the top-right corner, which the
  // reader's own controls occupy in fullscreen (pen + exit — see ReaderTab).
  // Without the reserve the centred row runs straight under them at any width
  // where it reaches the right edge: a phone always, a tablet in portrait. The
  // alternative — pushing the reader's controls down below this row — is what
  // made them unergonomic to reach.
  // SYMMETRIC padding, so `justify-center` actually centres.
  //
  // This used to be `pl-2` with `pr-24` in fullscreen — 96px reserved on the
  // right so the centred row could dodge the floating corner island. Padding
  // on one side only moves the centre: the bar sat ~48px left of the middle of
  // its own strip, on every platform, which is exactly the "doesn't feel
  // centred" that was reported. The island's controls live IN this row now, so
  // there is nothing to dodge and nothing to reserve.
  const anchor =
    "pointer-events-none absolute inset-x-0 top-2 z-20 flex justify-center px-2";

  // Read mode renders NOTHING here. The entry point (the pen) lives in the
  // reader's one right-hand island alongside the other document controls — see
  // ReaderTab. Keeping a second floating pill on that edge is what made the
  // controls collide and pushed the pen down the page.
  if (tool === "none") return null;

  return (
    <>
      {shapePaletteOpen && <ShapePalette />}
    <div className={anchor}>
      <div className="pointer-events-auto flex max-w-full items-center gap-0 overflow-x-auto rounded-lg border border-border bg-popover px-1 py-1 shadow-md">
        {TOOL_GROUPS.map((group, gi) => (
          <div key={gi} className="flex items-center gap-0.5">
            {gi > 0 && <Sep />}
            {group.map(({ id, label, Icon, key }) => (
              <ToolButton
                key={id}
                on={tool === id}
                onClick={() => setTool(id)}
                label={label}
                Icon={Icon}
                keyHint={key}
              />
            ))}
            {/* Shapes sit LAST, after Text. They belong with the marks by
                nature, but rendering them there put digit 7 to the left of
                digit 6 — a row of badges that doesn't count upwards is harder
                to scan than a slightly looser grouping. */}
            {gi === TOOL_GROUPS.length - 1 && <ShapePicker />}
          </div>
        ))}

        <Sep />

        {/* Session undo/redo — enabled from the InkModel's stack state. */}
        <Tip label="Undo (Ctrl+Z)">
          <button
            onClick={() => model?.undo()}
            disabled={!canUndo}
            aria-label="Undo"
            className={cn(ISLAND_BTN, INACTIVE, "disabled:opacity-30 disabled:hover:bg-transparent")}
          >
            <Undo2 className={ICON} />
          </button>
        </Tip>
        <Tip label="Redo (Ctrl+Y)">
          <button
            onClick={() => model?.redo()}
            disabled={!canRedo}
            aria-label="Redo"
            className={cn(ISLAND_BTN, INACTIVE, "disabled:opacity-30 disabled:hover:bg-transparent")}
          >
            <Redo2 className={ICON} />
          </button>
        </Tip>

        <Sep />

        <ColorSlots paper={paper} />

        {/* The active tool's options live in a FIXED-WIDTH slot. This is the
            layout-stability invariant: the island is centred, so if its width
            changed with the tool, every icon to the left of here would slide
            sideways — which is exactly what selecting the eraser used to do
            (it adds the Stroke/Area control). Reserving the widest tool's
            width means the row is the same size for every tool, and a tool
            button never moves under your finger. Sized for the eraser, the
            widest case (number field + size + the Stroke/Area segmented
            control, which was being clipped at a narrower reservation), with
            `overflow-x-auto` as the escape hatch if a future tool needs more —
            it scrolls inside the slot rather than moving the icons. */}
        <div className="flex w-[212px] shrink-0 items-center gap-2 overflow-x-auto">
          {/* Select mode shows the pen width too, so a SELECTED ink stroke can
              be live-resized (plan §3.6). The number field stays inline — the
              ink e2e fills it directly. Shapes share these three controls
              (width, size picker, colour): a shape is outline-only and drawn in
              the pen's weight, so a 3 pt rectangle matches a 3 pt stroke, and
              reusing the pen's option set keeps the reserved slot's width the
              same for every tool. */}
          {(tool === "pen" || tool === "select" || isShapeTool(tool)) && (
            <>
              <NumberField
                value={relToStrokePt(strokeRel)}
                onCommit={(pt) => setStrokeWidth(strokePtToRel(pt))}
                label="Pen width"
                list="gv-pen-widths"
                min={0.25}
                max={120}
                step={0.25}
              />
              <SizeWedge
                valueRel={strokeRel}
                onPickRel={setStrokeWidth}
                presetsPt={PEN_WIDTH_POINTS}
                ptToRel={strokePtToRel}
                color={color}
                kind="Pen"
              />
              <AnyColorPicker />
            </>
          )}
          {tool === "highlighter" && (
            <>
              <NumberField
                value={relToStrokePt(highlighterRel)}
                onCommit={(pt) => setHighlighterWidth(strokePtToRel(pt))}
                label="Highlighter width"
                list="gv-highlighter-widths"
                min={2}
                max={120}
                step={1}
              />
              <SizeWedge
                valueRel={highlighterRel}
                onPickRel={setHighlighterWidth}
                presetsPt={HIGHLIGHTER_WIDTH_POINTS}
                ptToRel={strokePtToRel}
                color={color}
                kind="Highlighter"
              />
              <AnyColorPicker />
            </>
          )}
          {tool === "eraser" && (
            <>
              <NumberField
                value={relToStrokePt(eraserRel)}
                onCommit={(pt) => setEraserRadius(strokePtToRel(pt))}
                label="Eraser radius"
                list="gv-eraser-radii"
                min={2}
                max={120}
                step={1}
              />
              <SizeWedge
                valueRel={eraserRel}
                onPickRel={setEraserRadius}
                presetsPt={ERASER_RADIUS_POINTS}
                ptToRel={strokePtToRel}
                color="#9aa0a6"
                kind="Eraser"
              />
              <EraseModeToggle mode={eraseMode} onChange={setEraseMode} />
            </>
          )}
          {tool === "text" && (
            <NumberField
              value={relToFontPt(fontSizeRel)}
              onCommit={(pt) => setFontSize(fontPtToRel(pt))}
              label="Font size"
              list="gv-font-sizes"
              min={4}
              max={400}
              step={1}
            />
          )}
          {tool === "fill" && (
            <>
              {/* The bucket paints in the pen's colour, so the picker has to be
                  here — without it, choosing a fill colour meant switching to
                  the pen, changing it, and switching back. There is no size to
                  set: the area decides that. */}
              <AnyColorPicker />
              <span className="ml-1 flex shrink-0 items-center gap-1 whitespace-nowrap font-mono text-[10px] text-muted-foreground">
                tap an enclosed area
              </span>
            </>
          )}
          {tool === "lasso" && (
            <span className="ml-1 flex shrink-0 items-center gap-1 whitespace-nowrap font-mono text-[10px] text-primary">
              <Sparkles className="h-3 w-3" /> circle → AI
            </span>
          )}
        </div>

        <Sep />

        {/* Everything secondary opens BELOW in a popover so the row stays one line. */}
        <Popover>
          <Tip label="More annotation options">
            <PopoverTrigger asChild>
              <button aria-label="More annotation options" className={cn(ISLAND_BTN, INACTIVE)}>
                <MoreHorizontal className={ICON} />
              </button>
            </PopoverTrigger>
          </Tip>
          <PopoverContent align="center" className="w-52 space-y-3">
            {tool === "text" && (
              <label className="flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
                Font
                <select
                  value={fontFamily}
                  onChange={(e) => setFont(e.target.value)}
                  aria-label="Font"
                  className={SELECT_CLS}
                >
                  {fonts.map((f) => (
                    <option key={f} value={f} style={{ fontFamily: fontStack(f) }}>
                      {fontLabel(f)}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {tool === "pen" && (
              <>
                <ToggleRow
                  on={pressure}
                  onClick={() => setPressure(!pressure)}
                  label="Stylus pressure"
                  Icon={Feather}
                />
                <SmoothingControl value={smoothing} onChange={setSmoothing} />
              </>
            )}
            {/* Shape styling. It lives HERE rather than on the row because the
                island's per-tool options slot is a fixed width — a hard layout
                invariant — and three more controls would not fit. */}
            {isShapeTool(tool) && <ShapeStyleControls />}
            <AddColorSlot />
            {/* A standing surface for a shape-heavy workflow — see ShapePalette.
                It lives under ⋯ because most sessions never need it; the one
                shape button on the row covers occasional use. */}
            <ToggleRow
              on={shapePaletteOpen}
              onClick={() => setShapePaletteOpen(!shapePaletteOpen)}
              label="Shape palette"
              Icon={Shapes}
            />
            {/* The stored field stays `palmReject` (no migration); the control reads
                as what it now is — an opt-in to draw with a finger. Off (the default)
                ⇒ palm rejection ON ⇒ the pen inks and the hand navigates. */}
            <ToggleRow
              on={!palmReject}
              onClick={() => setPalmReject(!palmReject)}
              label="Draw with finger"
              Icon={Hand}
            />
          </PopoverContent>
        </Popover>

        {/* The trailing group.
            In FULLSCREEN this bar is the only chrome on the page, so it carries
            the two controls that used to float in a corner island above it:
            the mode switch, rightmost, and the way out. One bar means nothing
            has to be positioned around anything else, and nothing clickable
            sits over the paper.
            WINDOWED it stays the compact ✕. The doc-controls island is still on
            screen there with its own Read/Write, and two segmented switches
            saying the same thing 40px apart is worse than one plus a ✕. */}
        {fullscreen ? (
          // STICKY to the right edge of the scrolling row. The row is
          // `overflow-x-auto`, and on a 393px phone it really does scroll — so
          // simply appending these put "Exit fullscreen" off the end of a bar
          // you had to swipe sideways to find. In fullscreen that is the only
          // way out of the mode. Sticky keeps the tools scrolling underneath
          // while the mode switch and the exit stay pinned where a thumb
          // expects them. The background is opaque for the same reason: tools
          // pass BEHIND this group, not through it.
          <div className="sticky right-0 ml-auto flex shrink-0 items-center gap-0 bg-popover pl-1">
            <Sep />
            <ModeSwitch on className="ml-0.5 shrink-0" />
            <Tip label="Exit fullscreen — Esc">
              <button
                onClick={() => useShell.getState().toggleFullscreen()}
                aria-label="Exit fullscreen"
                className={cn(ISLAND_BTN, INACTIVE, "ml-0.5")}
              >
                <Minimize2 className={ICON} />
              </button>
            </Tip>
          </div>
        ) : (
          <Tip label="Done annotating">
            <button
              onClick={() => setTool("none")}
              aria-label="Done annotating"
              className={cn(ISLAND_BTN, INACTIVE)}
            >
              <X className={ICON} />
            </button>
          </Tip>
        )}

        {/* Free-entry suggestions for the size fields (Word / GDocs style). */}
        <datalist id="gv-pen-widths">
          {PEN_WIDTH_POINTS.map((p) => (
            <option key={p} value={p} />
          ))}
        </datalist>
        <datalist id="gv-highlighter-widths">
          {HIGHLIGHTER_WIDTH_POINTS.map((p) => (
            <option key={p} value={p} />
          ))}
        </datalist>
        <datalist id="gv-eraser-radii">
          {ERASER_RADIUS_POINTS.map((p) => (
            <option key={p} value={p} />
          ))}
        </datalist>
        <datalist id="gv-font-sizes">
          {FONT_SIZE_POINTS.map((p) => (
            <option key={p} value={p} />
          ))}
        </datalist>
      </div>
      </div>
    </>
  );
}
